import { documentService } from '../services/documentService';
import { patchVoiceSegmentInDraft } from '../hooks/useConversationDraft';
import { logger } from './logger';
import { userErrorMessage } from './userError';

/**
 * Voice work that OUTLIVES the React tree (#368 — Audio Transcription Failure on
 * Navigation to Main Page).
 *
 * The fire-and-forget composer machinery (Key Pattern 15) survives navigation
 * WITHIN the shell — conversation switches, panel opens, even a "closed" chat —
 * because MainOfficeView stays mounted. The LAUNCHER at `/home`, however, is a
 * separate route rendered OUTSIDE WorkspaceShell: going there UNMOUNTS the whole
 * shell. The in-flight `fetch` to /api/audio/transcribe kept running (fetch is
 * not tied to React), but its resolver was a closure over component state:
 *
 *   • `lastConvIdForDraftRef.current` still held the origin conversation, so the
 *     resolver took the "I'm still viewing the origin" branch and called
 *     `setVoiceSegments` on an UNMOUNTED component — a silent no-op in React 18.
 *     The result was dropped on the floor: the persisted draft kept the clip at
 *     `status: 'transcribing'` forever, so returning showed a stuck spinner.
 *   • The pending-send registry (`pendingSendConvIds`) was component state, so a
 *     send queued behind that transcription died with the unmount — the message
 *     was never sent and never appeared in the thread.
 *
 * This module is the component-independent half, modelled on
 * `utils/backgroundDelete.js` (#79): a plain module the shell registers into, so
 * the work keeps running — and lands somewhere durable — when the shell is gone.
 *
 * SCOPE (deliberate): the TRANSCRIPTION completes in the background with no shell
 * mounted, and its result is written straight into the origin conversation's
 * persisted draft. The queued SEND is made durable here and fires the moment the
 * shell remounts (the user returns), which is what the card asks for — the
 * message "appears in the conversation history on return". Dispatching a RAG turn
 * with no shell mounted would mean duplicating `handleRagMessage` (optimistic
 * bubbles, per-conversation buffers, tool-step traces, streamed assistant saves)
 * into a second, unowned code path — a far larger risk than the bug it fixes, and
 * with no surface to render the streamed reply into.
 */

// ── The in-flight job table ─────────────────────────────────────────────────
// segmentId -> { blob, originConversationId, labels }. A job is removed only
// AFTER its result has been delivered, so "no job for this segment" reliably
// means "nothing is coming" (that is what makes orphan detection safe).
const jobs = new Map();

// The mounted shell's patch handler, if any. There is exactly one MainOfficeView
// in the tree, so this is a single slot rather than a listener set.
let sink = null;

// '__new__' adoption redirect (#290). Lives here rather than in a component ref
// so a resolver that settles with the shell unmounted resolves the same origin
// the mounted path would have. Module state, not storage: an adoption only
// matters for clips recorded in this page session.
let adoptedNewConversationId = null;

const DEFAULT_LABELS = {
    inaudible: '[inaudible]',
    failed: 'Transcription failed',
};

/**
 * Register the mounted shell's segment-patch handler. Returns an unregister
 * function for the effect cleanup. While a sink is registered every settled job
 * is handed to it (preserving the live-state / draft routing MainOfficeView
 * already does); with no sink the module writes to the draft itself.
 */
export function registerTranscriptionSink(fn) {
    sink = fn;
    return () => { if (sink === fn) sink = null; };
}

/** Is a transcription for this segment still running? */
export function hasInFlightTranscription(segmentId) {
    return jobs.has(segmentId);
}

/** Test/diagnostic helper — ids of every segment still transcribing. */
export function inFlightSegmentIds() {
    return Array.from(jobs.keys());
}

/** #290: redirect resolvers that captured a null ('__new__') origin. */
export function setAdoptedNewConversationId(id) {
    adoptedNewConversationId = id || null;
}

export function getAdoptedNewConversationId() {
    return adoptedNewConversationId;
}

/** The conversation a job's patch belongs to, after the #290 adoption redirect. */
function effectiveOrigin(originConversationId) {
    return originConversationId ?? adoptedNewConversationId ?? null;
}

function deliver(segmentId, patch, origin) {
    if (sink) {
        try {
            sink(segmentId, patch, origin);
            return;
        } catch (err) {
            logger.error('VOICE', 'Transcription sink threw — falling back to the draft:', err);
        }
    }
    // No shell mounted (the user is on the launcher, or the sink blew up): write
    // the result straight into the origin's persisted draft so it is waiting for
    // them when they come back.
    patchVoiceSegmentInDraft(origin, segmentId, patch);
}

/**
 * Upload + transcribe a clip in the background, independently of the React tree.
 * Never throws and never returns a rejected promise — a failure is delivered as
 * a `status: 'failed'` patch so the clip is visibly failed rather than silently
 * lost.
 */
export function startTranscription({ segmentId, blob, originConversationId = null, mimeType = null, labels = {} }) {
    if (!segmentId || !blob) return Promise.resolve();
    const text = { ...DEFAULT_LABELS, ...labels };
    jobs.set(segmentId, { originConversationId });

    return (async () => {
        let patch;
        try {
            // #377: attribute the transcription's energy cost to the conversation
            // the clip was recorded in. Null on a brand-new chat ('__new__'),
            // which genuinely has no id yet — the receipt claims that row back.
            const result = await documentService.uploadAndTranscribeAudio(
                blob, originConversationId,
            );
            patch = {
                status: 'ready',
                audioUrl: documentService.getAudioStreamUrl(result.audio_url),
                gcsPath: result.gcs_path,
                transcription: result.transcription || text.inaudible,
                mimeType: result.mime_type || mimeType || 'audio/webm',
            };
        } catch (err) {
            logger.error('VOICE', 'Segment transcription failed:', err);
            patch = { status: 'failed', error: userErrorMessage(err) };
        }
        // Deliver BEFORE forgetting the job: a shell remounting in between must
        // never see "still transcribing in the draft, no job running" and
        // mistake a landed result for an orphan.
        try {
            deliver(segmentId, patch, effectiveOrigin(originConversationId));
        } finally {
            jobs.delete(segmentId);
        }
    })();
}

// ── Durable pending-send intents ────────────────────────────────────────────
// "Guaranteed Delivery" (Key Pattern 15) registers the ORIGIN conversation as
// pending when Send is clicked while a clip is still transcribing. That registry
// was component state, so the launcher round-trip erased the intent. Mirroring it
// into localStorage makes it survive the unmount — the shell re-seeds from here
// on mount and flushes as soon as the clip has settled.
const PENDING_KEY = 'omnis:pending-voice-sends';

/** The set of origin keys (conversation id, or '__new__') with a queued send. */
export function readPendingSends() {
    try {
        const raw = JSON.parse(localStorage.getItem(PENDING_KEY) || '[]');
        return new Set(Array.isArray(raw) ? raw.filter((k) => typeof k === 'string' && k) : []);
    } catch (_) {
        return new Set();
    }
}

/** Mirror the live registry into storage. Called on every add/remove. */
export function writePendingSends(keys) {
    try {
        const list = Array.from(keys || []);
        if (list.length === 0) localStorage.removeItem(PENDING_KEY);
        else localStorage.setItem(PENDING_KEY, JSON.stringify(list));
    } catch (_) { /* ignore — a quota/private-mode failure must not break Send */ }
}

/** Test-only: drop every module-level job, sink and adoption redirect. */
export function __resetBackgroundTranscription() {
    jobs.clear();
    sink = null;
    adoptedNewConversationId = null;
}

export default startTranscription;
